import { BehaviorSubject } from "rxjs";
import { container, singleton } from "tsyringe";
import { UserService } from "./user-service";
import { uuidv4 } from '../utils/helpers';
import { Poll } from "../models/poll";
import { QnaAnswer, QnaQuestion, QnaQuestionUpvote } from "../models/qna";
import { filter } from "rxjs/operators";
const Primus = require('../thirdparty/primus');

@singleton()
export class PTSocketService {

    // Emit whenever the socket has connected. The value will be the allocated guid.
    private socketConnected$ = new BehaviorSubject<string>(null);
    private limitedRateChat$ = new BehaviorSubject<any>(null);
    private newMsgChat$ = new BehaviorSubject<any>(null);
    private deleteMsgChat$ = new BehaviorSubject<any>(null);
    private pollUpdate$ = new BehaviorSubject<{ notificationType: 'CREATE' | 'START' | 'END' | 'DELETE', pollData: Poll }>(null);
    public sharedVideo$ = new BehaviorSubject<any>(null);
    public eventRoster$ = new BehaviorSubject<any>(null);
    public eventState$ = new BehaviorSubject<any>(null);

    private previousMessageTimeStamp = Date.now();

    private qnaEvents = {
        questionAddUpdate$: new BehaviorSubject<{ qnaData: QnaQuestion }>(null),
        questionDeleteUpdate$: new BehaviorSubject<{ qnaData: { questionId: string } }>(null),
        questionUpvoteUpdate$: new BehaviorSubject<{ actionType: 'upvote', qnaData: QnaQuestionUpvote }>(null),
        answerUpdate$: new BehaviorSubject<{ actionType: 'add', qnaData: { questionId: string, answerCount: number, replyCount: number } & QnaAnswer }>(null)
    };

    // using DI in constructor would require to eject the app
    private userService = container.resolve(UserService);

    private socket: any;

    public get socketGuidValue() {
        return this.socketConnected$.value;
    }

    public get socketConnectedObservable() {
        return this.socketConnected$.asObservable();
    }

    public get limitedRateChatObservable() {
        return this.limitedRateChat$.asObservable();
    }

    public get newMsgChatObservable() {
        return this.newMsgChat$.asObservable();
    }

    public get deleteMsgChatObservable() {
        return this.deleteMsgChat$.asObservable();
    }

    public get pollUpdateObservable() {
        return this.pollUpdate$.asObservable();
    }

    public get qnaEventsObservables() {
        return {
            questionAddUpdateObservable: this.qnaEvents.questionAddUpdate$.pipe(filter(q => !!q)),
            questionDeleteUpdateObservable: this.qnaEvents.questionDeleteUpdate$.pipe(filter(q => !!q)),
            questionUpvoteUpdateObservable: this.qnaEvents.questionUpvoteUpdate$.pipe(filter(q => !!q)),
            answerUpdateObservable: this.qnaEvents.answerUpdate$.pipe(filter(q => !!q))
        };
    }

    public get rosterEventObservable() {
        return this.eventRoster$.asObservable();
    }

    public async connectSocket(eventId: string, eventAccessToken: string, socketUrl: string) {
        if (this.socket) {
            return;
        }
        this.socket = Primus.connect(socketUrl, {
            reconnect: {
                max: 30000,
                min: 2000,
                retries: 10
            },
            ping: 5000
        });
        this.socket.on('open', () => {
            // if(self._selfclosed){
            //     this.socket.end();
            // }else{
            //     self.currentRetryCount = 0;
            //     if (self.SELF_GUID) {
            //         options.endpoint_guid = self.SELF_GUID;
            //     }
            //     self.sendEvent(authMessageType, options);
            //     self.registry.publish("events/websocket/open", [options]);
            // }

            // Authenticate the user on the given event in the socket
            this.socket.emit('a2mevent.notification.register', {
                event_id: eventId,
                access_token: eventAccessToken,
                user: {
                    id: this.userService.user.id,
                    username: this.userService.user.username,
                    first_name: this.userService.user.firstName,
                    last_name: this.userService.user.lastName,
                    email: this.userService.user.emailId,
                    time_format: 12,
                    full_name: `${this.userService.user.firstName} ${this.userService.user.lastName}`,
                    is_user: true,
                    is_authenticated: true,
                    pc: 'bjn'
                }
            });
            console.log('open');
        });
        this.socket.on("a2mevent.notification.guid_assigned", async (e) => {
            console.log('PEOPLE guid_assigned', e.guid);
            this.socketConnected$.next(e.guid);
        });
        this.socket.on("a2mevent.notification.loginError", () => console.log('loginError'));

        this.socket.on("a2mevent.notification.msg", (data) => {
            // console.log('a2mevent.notification.msg', data);

            this.previousMessageTimeStamp = data?.previousMessageTimeStamp || Date.now();

            // check for CHAT msg deleted
            if (data.body) {
                const body = JSON.parse(data.body) as { msgType: string, msgBody: any };
                if (body?.msgType === 'Event_Chat' && body?.msgBody?.status === 'DELETED') {
                    // update deleteChat queue
                    this.deleteMsgChat$.next(body?.msgBody?.msgId);
                } else if (body?.msgType === 'POLL') {
                    this.pollUpdate$.next(body?.msgBody);
                } else if (body?.msgType === 'Event_QnA' && body?.msgBody) {
                    if (body.msgBody.notificationType === 'question') {
                        if (body.msgBody.actionType === 'add') {
                            this.qnaEvents.questionAddUpdate$.next(body.msgBody);
                        } else if (body.msgBody.actionType === 'delete') {
                            this.qnaEvents.questionDeleteUpdate$.next(body.msgBody);
                        } else if (body.msgBody.actionType === 'upvote') {
                            this.qnaEvents.questionUpvoteUpdate$.next(body.msgBody);
                        }
                    } else if (body.msgBody.notificationType === 'answer') {
                        if (body.msgBody.actionType === 'add') {
                            this.qnaEvents.answerUpdate$.next(body.msgBody);
                        }
                    }
                } else if (body?.msgType === 'Video_Share' && body?.msgBody) {

                    // get video URL and progress
                    console.log('video share MSG', body.msgBody);

                    body.msgBody.isSelf = body.msgBody.endpointGuid === this.socketGuidValue;

                    this.sharedVideo$.next(body.msgBody);
                    // set people as sharing video
                    // TODO

                } else if (body?.msgType === 'Event_Roster' && body?.msgBody) {
                    this.eventRoster$.next(body.msgBody);
                } else if (body?.msgType === 'Event_State' && body?.msgBody) {
                    this.eventState$.next(body.msgBody);
                }
            }
            // var timestampInformation = {
            //     currentMessageTimestamp : data.timestamp,
            //     previousMessageTimestamp : data.previousMessageTimestamp
            // };
            // if(self.checkForMissedNotification(timestampInformation)) {
            //     Logger.info("Previously sent push notification with timestamp", timestampInformation.previousMessageTimestamp, "not received");
            //     self.registry.publish("a2mevent.notification.notificationMissed");
            // } else {
            //     try {
            //         self.registry.publish("a2mevent.notification.msg", [data]);
            //     } catch (e) {
            //         Logger.error("Error in publishing event, which means there is some error in some consumer of the event : " + JSON.stringify(data.body), JSON.stringify(e));
            //     }
            //     if (data.body) {
            //         try {
            //             var evtBody = JSON.parse(data.body);
            //             self.registry.publish(evtBody.msgType, [evtBody.msgBody]);
            //         } catch (e) {
            //             Logger.error("Push notification body is not a JSON or error in consumer of event ", e, " Body of notificaton ", data.body);
            //         }

            //     }
            // }
            // self.lastNotificationReceivedTimestamp = timestampInformation.currentMessageTimestamp;
        });

        this.socket.on("a2mevent.notification.update.success", (data) => {
            // try {
            //     self.registry.publish("a2mevent.notification.update.success", [data]);
            // } catch (e) {
            //     Logger.error("Error in publishing event, which means there is some error in some consumer of the event : " + JSON.stringify(data.body), JSON.stringify(e));
            // }
        });
        this.socket.on('end', () => {
            //This happends when connection is closed and no more reconnects will be attempted or when client is offline in which case we reconnect once he is online
            // self.isClosed = true;
            // if (self.currentRetryCount >= self.retries) {
            //     Logger.error("Websocket closed, will not reconnect.");
            //     if (!self._selfclosed) {
            //         Logger.error("Websocket closed unexpectedly. But not initiated by client.");
            //         self.registry.publish("events/websocket/closed/unexpected", []);
            //     } else {
            //         Logger.error("Websocket closed, offline.");
            //         self.registry.publish("events/websocket/closed/offline");
            //     }
            // } else {
            //     Logger.info("Closing Websocket.");
            //     self.registry.publish("events/websocket/closed/normal");
            // }
            console.log('end');

        });
        this.socket.on("error", (e) => {
            console.log('error', e);
            // Logger.error("SockJS error occured", e);
            // self.registry.publish("events/websocket/error", [e]);
        });
        this.socket.on("close", () => {
            console.log('close')
            // self.isClosed = true;
            // if (!self._selfclosed) {
            //     Logger.error("Websocket closed, but will reconnect...");
            //     self.registry.publish("events/websocket/closed/willreconnect", [self.currentRetryCount]);
            // }
        });

        this.socket.on('reconnect scheduled', (opts) => {
            // Logger.warn("Reconnecting in %d ms for atempt %d out of %d", opts.scheduled, opts.attempt, opts.retries);
        });

        this.socket.on('reconnect', (opts) => {
            // self.currentRetryCount = self.currentRetryCount + 1;
            // Logger.warn("Reconnection started for atempt %d", opts.attempt);
            // self.registry.publish("events/websocket/closed/reconnecting", [self.currentRetryCount]);

            console.log('reconnect');
        });

        this.socket.on('reconnected', (opts) => {
            // Logger.warn("It took %d ms to reconnect and %d attemps", opts.duration , opts.attempt);

            console.log('reconnected', opts);
        });


        this.socket.on("a2mevent.chat.msg", (data) => {
            try {
                console.log('chat.msg', data);
                this.newMsgChat$.next(data);
            } catch (e) {
                console.error("Error in publishing event, which means there is some error in some consumer of the event : " + JSON.stringify(data.body), JSON.stringify(e));
            }
        });

        this.socket.on("a2mevent.chat.rateLimited", (data) => {
            try {
                console.log('chat.rateLimited', data);
                this.limitedRateChat$.next(data);
            } catch (e) {
                console.error("Error in publishing event, which means there is some error in some consumer of the event : " + JSON.stringify(data.body), JSON.stringify(e));
            }
        });
    }

    public disconnectSocket() {
        if (!this.socket) {
            return;
        }

        try {
            this.socket.destroy();
        } catch (e) {
            console.warn('Error while destroying PT socket', e);
        } finally {
            this.socket = null;
        }
    }

    public sendChatMessage(body: string, channel: 'backstage' | 'moderator' | 'private' | 'public' = 'moderator', receiver: { guid: string, name: string, role: 'attendee' } = null) {


        const data = {
            body: body,
            sender:{
                guid: "f73d639b-afb7-461f-9102-5ab64b50704f",
                name: "Me",
                role: "moderator",
                userId: 4676045,
            },
            event:{
                guid: uuidv4(),
            },
            privateChat: false,
            moderatorPresenterChat: channel === 'backstage',
            moderatorChat: channel === 'moderator',
            id: uuidv4(),
            timestamp: Date.now(),
        };
        this.newMsgChat$.next(data);


        // const user = this.userService.user;
        // const sender = { guid: uuidv4(), name: user.username, role: 'moderator', userId: user.id.toString() };
        // if (channel === 'backstage') {
        //     this.socket.emit("a2mevent.chat.msg", {
        //         msg: body,
        //         sender,
        //         event: { guid: uuidv4(), timestamp: Date.now(), previousMessageTimeStamp: this.previousMessageTimeStamp },
        //         tag: "moderator_panelist"
        //     });
        // } else if (channel === 'moderator') {
        //     this.socket.emit("a2mevent.chat.msg", {
        //         msg: body,
        //         sender,
        //         event: { guid: uuidv4(), timestamp: Date.now(), previousMessageTimeStamp: this.previousMessageTimeStamp },
        //         tag: "moderator"
        //     });
        // } else if (channel === 'private') {
        //     this.socket.emit("a2mevent.chat.msg", {
        //         msg: body,
        //         receiver,
        //         event: { guid: uuidv4(), timestamp: Date.now(), previousMessageTimeStamp: this.previousMessageTimeStamp },
        //         tag: "moderator_reply"
        //     });
        // } else {
        //     this.socket.emit("a2mevent.chat.msg", {
        //         msg: body,
        //         sender,
        //         event: { guid: uuidv4(), timestamp: Date.now(), previousMessageTimeStamp: this.previousMessageTimeStamp }
        //     });
        // }

    }


    // socket event is generated by ptApi.shareVideoAction
    // public videoSharingEvent(playStatus, offset, media) {


    //     const user = this.userService.user;
    //     const sender = { guid: uuidv4(), name: user.username, role: 'moderator', userId: user.id.toString() };

    //     const body = { msgType: "Video_Share", msgBody: {
    //         instanceId: media?.id || '',
    //         eventId: null,
    //         initiatedTime: Date.now(),
    //         offset: offset,
    //         contentId: 0,
    //         hdsUrl: media?.mp4 || '',
    //         hlsUrl: media?.mp4 || '',
    //         hlsVodUrl: media?.mp4 || '',
    //         state: playStatus,
    //         notificationTime: Date.now()
    //         }
    //     };

    //     if (this.socket) {
    //         this.socket.emit("a2mevent.notification.msg", {
    //             body: JSON.stringify(body),
    //             sender: sender,
    //             receiver: null,
    //             event: { guid: uuidv4(), timestamp: Date.now(), previousMessageTimeStamp: this.previousMessageTimeStamp, directToEP: true },
    //             tag: null,
    //         });
    //     }

    // }
}
